import { Box, Stack, Typography } from "@mui/material";
// config
import colors from "../../../config/colors";

const Demographics = ({ details }) => {
  // Hardcoded dummy data
  const genderObj = details?.gender_demographics || {};

  const genderKeyToMeta = {
    MALE: { label: "Male", color: "#FFAB7C" },
    FEMALE: { label: "Female", color: "#FF7B9A" },
    UNKNOWN: { label: "Other", color: "#9772FF" },
  };

  const rawGenderData = Object.entries(genderKeyToMeta).map(([key, meta]) => ({
    ...meta,
    percentage: Number(genderObj?.[key]) || 0,
  }));

  // Optional normalization if backend sends fractions (0..1) instead of percent (0..100)
  const rawSum = rawGenderData.reduce((s, x) => s + x.percentage, 0);
  const looksLikeFraction = rawSum > 0 && rawSum <= 1.01;

  // step 1: convert to percent units (0..100-ish)
  const base = rawGenderData.map((x) => ({
    ...x,
    percentage: looksLikeFraction ? x.percentage * 100 : x.percentage,
  }));

  // step 2: normalize to sum=100
  const sum = base.reduce((s, x) => s + x.percentage, 0);
  let normalized =
    sum > 0
      ? base.map((x) => ({ ...x, percentage: (x.percentage / sum) * 100 }))
      : base;

  // step 3: make displayed sum exactly 100.0 (1-decimal rounding)
  normalized = (() => {
    const rounded = normalized.map((x) => ({
      ...x,
      percentage: Math.round(x.percentage * 10) / 10, // 1 decimal
    }));

    const roundedSum = rounded.reduce((s, x) => s + x.percentage, 0);
    const diff = Math.round((100 - roundedSum) * 10) / 10; // diff in 0.1 steps
    if (diff === 0) return rounded;

    // add diff to the largest segment to avoid negative values
    const idx = rounded.reduce(
      (bestIdx, x, i, arr) =>
        x.percentage > arr[bestIdx].percentage ? i : bestIdx,
      0,
    );

    const next = [...rounded];
    next[idx] = {
      ...next[idx],
      percentage: Math.max(0, next[idx].percentage + diff),
    };
    return next;
  })();

  const genderData = normalized;

  const ageObj = details?.age_demographics || {};
  const ageRangeData = Object.entries(ageObj)
    .map(([label, percentage]) => ({
      label,
      percentage: Number(percentage) || 0,
    }))
    .filter((x) => x.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 3);

  const citiesData = (details?.city_state_demographics || [])
    .map((item) => ({
      label: `${item.city}${item.state ? `, ${item.state}` : ""}`,
      percentage: Number(item.value) || 0,
    }))
    .filter((x) => x.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 4);

  // Chart dimensions for donut chart
  const size = 100;
  const radius = 40;
  const strokeWidth = 11;
  const centerX = size / 2;
  const centerY = size / 2;

  const createArcPath = (startAngle, endAngle, radius) => {
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  let currentAngle = 0;
  const genderSegments = genderData.map((item) => {
    const angle = (item.percentage / 100) * 360;
    return { ...item, angle };
  });

  const BarChart = ({ data, maxPercentage }) => {
    return (
      <Stack gap={1}>
        {data.map((item, index) => (
          <Box key={index}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography
                fontSize={10}
                fontWeight={400}
                color={colors.gray600}
                lineHeight="14px"
              >
                {item.label}:
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1} width="100%">
              <Box
                sx={{
                  width: `${(item.percentage / (maxPercentage > 0 ? maxPercentage : 1)) * 100}%`,
                  backgroundColor: "#F38273",
                  height: "8px",
                }}
              />
              <Typography fontSize={12} fontWeight={500}>
                {item.percentage.toFixed(1).replace(/\.0$/, "")}%
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography
        fontSize={12}
        fontWeight={400}
        lineHeight="20px"
        color={colors.gray600}
      >
        Audience Demographics:
      </Typography>

      <Stack direction="row" gap={1} alignItems="flex-start">
        {/* Gender Section */}
        <Box
          sx={{
            flex: 1,
            padding: "8px 12px",
            border: `1px solid ${colors.gray100}`,
            borderRadius: "4px",
            backgroundColor: colors.white,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            height: "100%",
          }}
        >
          <Typography fontSize={12} fontWeight={400} lineHeight="20px">
            Gender:
          </Typography>
          <Stack direction="column" gap={1} alignItems="center">
            <Box sx={{ position: "relative" }}>
              <svg width={size} height={size}>
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill="none"
                  stroke={colors.gray100}
                  strokeWidth={strokeWidth}
                />
                {genderSegments.map((segment, index) => {
                  const startAngle = currentAngle;
                  const endAngle = currentAngle + segment.angle;
                  currentAngle = endAngle;

                  return (
                    <path
                      key={index}
                      d={createArcPath(startAngle, endAngle, radius)}
                      fill="none"
                      stroke={segment.color}
                      strokeWidth={strokeWidth}
                    />
                  );
                })}
              </svg>
            </Box>
            <Stack gap={1} flex="1" width="100%">
              {genderData.map((item, index) => (
                <Stack key={index} direction="row" gap={1} alignItems="center">
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: item.color,
                      borderRadius: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    fontSize={12}
                    fontWeight={400}
                    color={colors.gray600}
                  >
                    {item.label}:
                  </Typography>
                  <Typography fontSize={12} fontWeight={500}>
                    {item.percentage.toFixed(1).replace(/\.0$/, "")}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>

        {/* Top Age Range Section */}
        <Box
          sx={{
            flex: 1,
            padding: "8px 12px",
            border: `1px solid ${colors.gray100}`,
            borderRadius: "4px",
            backgroundColor: colors.white,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            height: "100%",
          }}
        >
          <Typography fontSize={12} fontWeight={400} lineHeight="20px">
            Top Age Range:
          </Typography>
          <BarChart
            data={ageRangeData}
            maxPercentage={
              ageRangeData.length
                ? Math.max(...ageRangeData.map((d) => d.percentage))
                : 0
            }
          />
        </Box>

        {/* Top Towns/cities Section */}
        <Box
          sx={{
            flex: 1,
            padding: "8px 12px",
            border: `1px solid ${colors.gray100}`,
            borderRadius: "4px",
            backgroundColor: colors.white,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            height: "100%",
          }}
        >
          <Typography
            fontSize={12}
            fontWeight={400}
            lineHeight="20px"
            color={colors.gray600}
            mb={1}
          >
            Top Towns/cities:
          </Typography>
          <BarChart
            data={citiesData}
            maxPercentage={
              citiesData.length
                ? Math.max(...citiesData.map((d) => d.percentage))
                : 0
            }
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default Demographics;
